import { getAllVehiclesOptions } from '@/api/main/@tanstack/react-query.gen'
import { QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ErrorState from '@/components/Common/ErrorState'
import PageShell from '@/components/Common/PageShell'
import { Grid, LoadingOverlay, Tabs, Text, Image, Modal, Button, Box, Group } from '@mantine/core'
import { useControlUnitClient, useNavigationClient } from '@/api-proxy/useProxiedClient'
import { getRawPhotosOptions, getPhotoOptions as getRawPhotoOptions } from '@/api/navigationApi'
import { VEHICLE_IMAGE_REFETCH_INTERVAL_MS, VEHICLE_IMAGE_STALE_TIME_MS } from '@/constants'
import useBlobUrl from '@/hooks/useBlobUrl'
import { useEffect, useState, useRef } from 'react'
import FocusPanel from '@/components/Hardware/FocusPanel'
import { getFocusOptions, putFocusAbsoluteMutation } from '@/api/controlUnitApi'
import { extendKey } from '@/utils/queryKey'
import { useNavigate } from '@tanstack/react-router'
import { Route } from '@/routes/hardware.$source.$tab'
import { notifications } from '@mantine/notifications'
import { LevelResponse } from '@/api/controlUnitApi/types.gen'
import { Rectangle } from './types'
import RestartsPanel from './RestartsPanel'
import { SectionTitle } from '../Common/SectionTitle'
import { SlArrowLeft } from 'react-icons/sl'
import { useDisclosure, useElementSize, useMediaQuery } from '@mantine/hooks'
import IconButton from '../Common/IconButton'
import { FaSync } from 'react-icons/fa'
import { BiFullscreen } from 'react-icons/bi'
import PhotoComparisonOverlay from '../PhotoComparisonOverlay'
import { translateCamera, translateToNaturalScale } from './helpers'
import OtherPanel from './OtherPanel'
import { usePageTitle } from '@/hooks/usePageTitle'
import { CITY_NAME } from '@/meta'
import { useTranslation } from 'react-i18next'

function HardwareConfigurationPage() {
  const { t } = useTranslation(['hardware', 'common'])
  const { source, tab } = Route.useParams()
  const isMobile = useMediaQuery('(max-width: 768px)')

  const navigate = useNavigate()

  usePageTitle(`Hardware ${source} ${tab} | ${CITY_NAME}`)

  const [camera, setCamera] = useState<'Front Left' | 'Front Center' | 'Front Right'>('Front Left')
  const [focusIncrement, setFocusIncrement] = useState<number>(50)
  const [focusValue, setFocusValue] = useState<number | undefined>(undefined)
  const [savedFocusValue, setSavedFocusValue] = useState<number | undefined>(undefined)
  const [fullscreenModalOpened, { open: openFullscreenModal, close: closeFullscreenModal }] = useDisclosure(false)

  const [displaySaveButton, setDisplaySaveButton] = useState<boolean>(false)
  const [selectedRectangle, setSelectedRectangle] = useState<Rectangle | null>(null)
  const [rectangle, setRectangle] = useState<Rectangle | null>(null) // Single square
  const [naturalScaleRectangle, setNaturalScaleRectangle] = useState<Rectangle | null>(null)
  const [combinedViewHeight, setCombinedViewHeight] = useState<number | null>(null)
  const initialFocusRefs = useRef<Map<string, number>>(new Map())
  const imageElementRef = useRef<HTMLImageElement | null>(null)
  const { height, ref: combinedViewRef } = useElementSize()

  useEffect(() => {
    if (height !== 0) setCombinedViewHeight(height)
    return () => setCombinedViewHeight(null)
  }, [height])

  const { data: vehicles, isLoading: isLoadingVehicles } = useQuery({
    ...getAllVehiclesOptions(),
  })

  const invalidTab = tab !== 'restarts' && tab !== 'focus' && tab !== 'other'
  const invalidSource = !vehicles?.some(({ id }) => id === source)

  const client = useNavigationClient(source)
  const controlUnitClient = useControlUnitClient(source)
  const queryClient = useQueryClient()

  const COMBINED_PHOTO_FETCH_ENABLED = (tab === 'restarts' || tab === 'other') && !invalidSource && !invalidTab
  const VERTICAL_PHOTO_FETCH_ENABLED = tab === 'focus' && !invalidSource && !invalidTab

  const {
    data: combinedView,
    isLoading: isLoadingCombinedView,
    isRefetching: isRefetchingCombinedView,
    isError: isErrorCombinedView,
    refetch: refetchCombinedView,
  } = useQuery({
    ...extendKey(getRawPhotosOptions({ client }), source, tab),
    retry: 1,
    refetchInterval: VEHICLE_IMAGE_REFETCH_INTERVAL_MS,
    enabled: COMBINED_PHOTO_FETCH_ENABLED,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
  })

  const {
    data: rawVertical,
    isLoading: isLoadingRawVertical,
    isRefetching: isRefetchingRawVertical,
    refetch: refetchRawVertical,
    isFetching: isFetchingRawVertical,
  } = useQuery({
    ...extendKey(
      getRawPhotoOptions({ client, path: { camera: translateCamera(camera) } }),
      source,
      tab,
      camera,
      'initial-focus'
    ),
    retry: 1,
    enabled: VERTICAL_PHOTO_FETCH_ENABLED,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    gcTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const focusOpts = extendKey(
    getFocusOptions({ client: controlUnitClient, path: { camera_position: translateCamera(camera) } }),
    source,
    tab,
    camera
  )
  const focusLevelQueryKey: QueryKey = focusOpts.queryKey

  const { data: focusLevel, refetch: refetchFocusValue } = useQuery({
    ...focusOpts,
    enabled: VERTICAL_PHOTO_FETCH_ENABLED,
  })

  const { mutate: mutateFocusValue, isPending: isFocusMutationPending } = useMutation({
    ...putFocusAbsoluteMutation({ client: controlUnitClient, path: { camera_position: translateCamera(camera) } }),
    onSuccess: () => {
      const newFocusValue = focusValue ?? 0
      queryClient.setQueryData(focusLevelQueryKey, (prev: LevelResponse | undefined) => {
        if (prev === undefined) {
          throw new Error('Mutation attempted before fetching focus value')
        }
        return { ...prev, level: newFocusValue }
      })
      setSavedFocusValue(newFocusValue)
      setDisplaySaveButton(false)
      refetchFocusValue()
      notifications.show({
        title: t('focusValue'),
        message: t('focusValueSet'),
        color: 'green',
      })
    },
    onError: () => {
      notifications.show({
        title: t('focusValue'),
        message: t('failedToSetFocusValue'),
        color: 'red',
      })
    },
  })

  const {
    data: adjustedFocusPhoto,
    isLoading: isLoadingAdjustedFocusPhoto,
    isRefetching: isRefetchingAdjustedFocusPhoto,
    refetch: refetchAdjustedFocusPhoto,
    isFetching: isFetchingAdjustedFocusPhoto,
  } = useQuery({
    ...extendKey(
      getRawPhotoOptions({ client, path: { camera: translateCamera(camera) } }),
      source,
      tab,
      camera,
      'adjusted-focus',
      focusValue ?? 0
    ),
    enabled: VERTICAL_PHOTO_FETCH_ENABLED && isFocusMutationPending,
    staleTime: VEHICLE_IMAGE_STALE_TIME_MS,
    gcTime: VEHICLE_IMAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (focusLevel?.level !== undefined) {
      setFocusValue(focusLevel.level)
      setSavedFocusValue(focusLevel.level)

      // Store initial focus value for this specific camera
      const cameraKey = translateCamera(camera)
      if (!initialFocusRefs.current.has(cameraKey)) {
        initialFocusRefs.current.set(cameraKey, focusLevel.level)
      }
    }
  }, [focusLevel, camera])

  const handleSquareChange = (rectangle: Rectangle | null) => {
    setSelectedRectangle(rectangle)
    if (rectangle && imageElementRef.current) {
      setNaturalScaleRectangle(translateToNaturalScale(rectangle, imageElementRef.current))
    } else {
      setNaturalScaleRectangle(null)
    }
  }

  const resetToInitialFocus = () => {
    const cameraKey = translateCamera(camera)
    const initialValue = initialFocusRefs.current.get(cameraKey)

    if (initialValue !== undefined) {
      setFocusValue(initialValue)
      const hasChanged = savedFocusValue !== undefined && initialValue !== savedFocusValue
      setDisplaySaveButton(hasChanged)
    }
  }

  const horizontalUrl = useBlobUrl(combinedView as Blob | undefined)

  const verticalUrl = useBlobUrl(rawVertical as Blob | undefined)

  const adjustedFocusPhotoUrl = useBlobUrl(adjustedFocusPhoto as Blob | undefined)

  const handleTabChange = (value: string) => {
    navigate({
      to: `/hardware/${source}/${value}`,
    })
  }
  const handleFocusValueChange = (value: number) => {
    if (value > 9118) return

    if (value < 0) return

    setFocusValue(value)

    // Show save button if value differs from saved value
    const hasChanged = savedFocusValue !== undefined && value !== savedFocusValue
    setDisplaySaveButton(hasChanged)
  }

  if (isLoadingVehicles) return <LoadingOverlay visible={isLoadingVehicles} />

  if (invalidTab) {
    return (
      <PageShell title={t('hardwareConfiguration')}>
        <ErrorState title={t('invalidTab')} message={t('invalidTabMessage')} />
      </PageShell>
    )
  }

  if (invalidSource) {
    return (
      <PageShell title={t('hardwareConfiguration')}>
        <ErrorState title={t('invalidSource')} message={t('invalidSourceMessage')} />
      </PageShell>
    )
  }

  const title = (
    <Group justify="space-between" align="center">
      <SectionTitle order={1}>{t('hardwareConfigFor', { source: source.toUpperCase() })}</SectionTitle>
      <Button
        mb="md"
        leftSection={<SlArrowLeft />}
        onClick={() => navigate({ to: `/vehicles` })}
        size="sm"
        variant="subtle"
      >
        {t('back', { ns: 'common' })}
      </Button>
    </Group>
  )

  const isTrippleCameraViewLoading = isLoadingCombinedView || isRefetchingCombinedView

  const isOriginalPhotoLoading = isLoadingRawVertical || isRefetchingRawVertical || isFetchingRawVertical

  const isAdjustedPhotoLoading =
    isLoadingAdjustedFocusPhoto || isRefetchingAdjustedFocusPhoto || isFetchingAdjustedFocusPhoto

  if (!controlUnitClient) throw new Error('Control unit client not found')

  const columns = [
    <Grid.Col key="left-column" span={isMobile ? 12 : 6}>
      {(tab === 'restarts' || tab === 'other') && (
        <>
          <Box
            style={{
              position: 'relative',
              height: tab === 'restarts' || tab === 'other' ? '100vh' : 'auto',
            }}
          >
            {horizontalUrl && (
              <>
                <Image
                  src={horizontalUrl}
                  alt="Raw Photo"
                  ref={combinedViewRef}
                  onLoad={img => setCombinedViewHeight(img.currentTarget.height)}
                />
                <IconButton
                  label={t('reload')}
                  onClick={() => refetchCombinedView({})}
                  styles={{ root: { position: 'absolute', top: '10px', right: '10px' } }}
                  icon={<FaSync size={12} color="var(--mantine-color-gray-6)" />}
                />
                {combinedViewHeight && (
                  <IconButton
                    label={t('fullscreen')}
                    onClick={openFullscreenModal}
                    styles={{ root: { position: 'absolute', top: `${combinedViewHeight - 48}px`, right: '10px' } }}
                    icon={<BiFullscreen size={12} />}
                  />
                )}
              </>
            )}
            {isErrorCombinedView && !horizontalUrl && (
              <ErrorState
                title={t('imageFetchFailed')}
                message={t('couldNotLoadCameraImage')}
                showBackButton={false}
                width="100%"
                retryAction={
                  <Button variant="light" size="xs" mt="md" onClick={() => refetchCombinedView({})}>
                    {t('retry', { ns: 'common' })}
                  </Button>
                }
              />
            )}
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                display: isTrippleCameraViewLoading ? 'block' : 'none',
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                visibility: isTrippleCameraViewLoading ? 'visible' : 'hidden',
              }}
            >
              <LoadingOverlay visible={isTrippleCameraViewLoading} />
            </Box>
          </Box>
        </>
      )}
      {tab === 'focus' && (
        <PhotoComparisonOverlay
          imageUrl={verticalUrl}
          isLoading={isOriginalPhotoLoading}
          setSelectedSquare={handleSquareChange}
          selectedSquare={selectedRectangle}
          square={rectangle}
          setSquare={setRectangle}
          onImageLoad={img => {
            imageElementRef.current = img
          }}
        />
      )}
    </Grid.Col>,
    <Grid.Col key="right-column" span={isMobile ? 12 : 6}>
      <PageShell title={title} fullWidth>
        <Tabs
          value={tab}
          onChange={tab => {
            if (!tab) return
            handleTabChange(tab)
          }}
          inverted
        >
          <Tabs.List grow={false}>
            <Tabs.Tab value="restarts" size="md">
              {t('restarts')}
            </Tabs.Tab>
            <Tabs.Tab value="focus">{t('focus')}</Tabs.Tab>
            <Tabs.Tab value="other">{t('other')}</Tabs.Tab>
          </Tabs.List>
          <RestartsPanel client={controlUnitClient} />
          <FocusPanel
            camera={camera}
            setCamera={setCamera}
            focusIncrement={focusIncrement}
            setFocusIncrement={setFocusIncrement}
            focusValue={focusValue}
            setFocusValue={handleFocusValueChange}
            focusLoading={isFocusMutationPending}
            displaySaveButton={displaySaveButton}
            onSave={() => {
              if (focusValue !== undefined) {
                mutateFocusValue({ body: { level: focusValue }, path: { camera_position: translateCamera(camera) } })
              }
            }}
            originalImageUrl={verticalUrl}
            adjustedImageUrl={adjustedFocusPhotoUrl}
            selectedSquare={naturalScaleRectangle}
            onSquareChange={setSelectedRectangle}
            isLoading={isLoadingAdjustedFocusPhoto}
            onResetToInitial={resetToInitialFocus}
            initialFocusValue={initialFocusRefs.current.get(translateCamera(camera))}
            isOriginalLoading={isOriginalPhotoLoading}
            isAdjustedLoading={isAdjustedPhotoLoading}
            onRefetchOriginal={refetchRawVertical}
            onRefetchAdjusted={refetchAdjustedFocusPhoto}
          />
          <OtherPanel source={source} client={controlUnitClient} isRefetchingCombinedView={isRefetchingCombinedView} />
        </Tabs>
      </PageShell>
    </Grid.Col>,
  ]

  return (
    <>
      <Grid gutter="none">{isMobile ? columns.reverse() : columns}</Grid>
      <Modal
        opened={fullscreenModalOpened}
        onClose={closeFullscreenModal}
        title={
          <Group justify="space-between" align="center">
            <Text fw={500}>{t('combinedViewRaw')}</Text>
            <Button leftSection={<FaSync />} variant="outline" onClick={() => refetchCombinedView({})}>
              {t('reload')}
            </Button>
          </Group>
        }
        fullScreen
        radius={0}
        transitionProps={{ transition: 'fade', duration: 200 }}
      >
        <Image src={horizontalUrl} alt="Raw Photo" bdrs="md" />
      </Modal>
    </>
  )
}

export default HardwareConfigurationPage
